/* import __COLOCATED_TEMPLATE__ from './sq-drop-target.hbs'; */
/**

## Drop Target

`SqDropTargetComponent` allows you to designate a chunk of DOM as a drop
target, and specify an action to be called when something is dropped. This
action will receive the jQuery drop event as its only param.


```example
@title Basic Usage
@description You'll get the markup and styling for a nice looking drop target with hover
and "I have a file" styling, provided you bind the `file` attribute to the file
you'll be setting in your `dropped` callback.

@controller-properties
{
  actions: {
    dropped(event) {
      // do something
    }
  }
}

@hbs
{{sq-drop-target placeholderText=(t "common.dropTarget.placeholder")
                 fileSelectLinkOr=(t "common.dropTarget.fileSelectLinkOr")
                 fileSelectLinkText=(t "common.dropTarget.fileSelectLinkText")
                 dropped=(action "dropped")}}
```

```example
@title Restricting File Types
@description You can pass a `fileTypeWhitelist=` string to control what kind of files customers are allowed
to select via the link.

@controller-properties
{
  actions: {
    dropped() {
      //do something
    }
  }
}

@hbs
{{sq-drop-target placeholderText=(t "common.dropTarget.placeholder")
                 fileSelectLinkOr=(t "common.dropTarget.fileSelectLinkOr")
                 fileSelectLinkText=(t "common.dropTarget.fileSelectLinkText")
                 fileTypeWhitelist="png,gif,jpg,jpeg"
                 dropped=(action "dropped")}}
```

```example
@title Dismissing a File
@description Pass in `fileDismissed` action if you want to listen to file
dismiss event. This action will be triggered once the user clicks
the dismiss button and it will also set the local `file` attribute
to `null`

@controller-properties
{
  actions: {
    dropped() {
      //do something
    },
    fileDismissed() {
      //do something
    }
  }
}

@hbs
{{sq-drop-target placeholderText=(t "common.dropTarget.placeholder")
                 fileSelectLinkOr=(t "common.dropTarget.fileSelectLinkOr")
                 fileSelectLinkText=(t "common.dropTarget.fileSelectLinkText")
                 dropped=(action "dropped")
                 fileDismissed=(action "fileDismissed")}}
```

```example
@title Events
@description Sometimes we're less concerned about the full `dropped` callback, more strictly that a file
was actually dropped in. Use the `fileChanged` action, which returns both the file and a boolean
if it's valid.

@controller-properties
{
  actions: {
    fileChanged() {
      //do something
    }
  }
}

@hbs
{{sq-drop-target placeholderText=(t "common.dropTarget.placeholder")
                 fileSelectLinkOr=(t "common.dropTarget.fileSelectLinkOr")
                 fileSelectLinkText=(t "common.dropTarget.fileSelectLinkText")
                 fileChanged=(action "fileChanged")}}
```

@param {Boolean} [isInvalid=false] - Invalid state
@param {Boolean} [isDraggedOver=false] - true when the div has a file being dragged over it
@param {String} file - the file that has been dropped onto the drop target
@param {String} fileTypeWhitelist - comma separated list of accepted filetypes for this drop target
@param {String} placeholderText - placeholder text for the dropzone (i.e. "Drag your file here")
@param {String} fileSelectLinkOr - Text for "or"
@param {String} fileSelectLinkText - Text for the file select link (i.e. "Select a file from your computer")

*/

import { isBlank, isPresent } from '@ember/utils';
import { computed } from '@ember/object';
import Component from '@ember/component';
import $ from 'jquery';

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  classNames: ['drop-target'],
  classNameBindings: ['isDraggedOver:drop-target--is-dragged-over', 'hasFile:drop-target--has-file', 'isInvalid:drop-target--is-invalid'],
  isInvalid: false,
  isDraggedOver: false,
  file: null,
  fileTypeWhitelist: null,
  placeholderText: null,
  fileSelectLinkOr: null,
  fileSelectLinkText: null,
  hasFile: computed('file', function () {
    return this.get('file') !== null;
  }),
  didInsertElement(...args) {
    this._super(...args);
    // Ember does not attach a default handler for this event
    // for some reason, so we do it ourselves.
    // eslint-disable-next-line ember/no-jquery
    $(this.element).on('dragover.drop-target', function (e) {
      e.preventDefault();
      // ONCALL-9294 - When dragging from Chrome's download bar,
      // `e.dataTransfer.effectAllowed` will be set to 'copyMove', so we must set
      // the drop effect to one of these in order for the drop event to be triggered.
      e.dataTransfer.dropEffect = 'copy'; // eslint-disable-line no-param-reassign
    });
  },
  willDestroyElement(...args) {
    this._super(...args);
    $(this.element).off('.drop-target'); // eslint-disable-line ember/no-jquery
  },
  dragEnter(e) {
    e.preventDefault();
    this.set('isDraggedOver', true);
  },
  dragLeave() {
    this.set('isDraggedOver', false);
  },
  drop(e) {
    e.preventDefault();
    this.set('isDraggedOver', false);
    if (this.dropped) {
      this.dropped(e);
    }
    const file = e.dataTransfer.files[0];
    if (!file) {
      return;
    }
    const invalid = !this.validateFileType(file, this.get('fileTypeWhitelist'));
    this.set('isInvalid', invalid);
    if (this.fileChanged) {
      this.fileChanged(e, file, invalid);
    }
  },
  validateFileType(file, whitelist) {
    if (isBlank(whitelist)) {
      return true;
    }
    const fileTypes = whitelist.split(',');
    let valid = false;
    if (isPresent(file.type)) {
      valid = fileTypes.includes(file.type);
    }

    // fallback to file extension if the mime type does not match
    if (!valid) {
      if (fileTypes.includes('text/csv')) {
        valid = valid || /\.csv$/.test(file.name);
      }
      if (fileTypes.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
        valid = valid || /\.xlsx$/.test(file.name);
      }
    }
    return valid;
  },
  actions: {
    fileChanged(e) {
      const file = this.get('file');
      const invalid = !this.validateFileType(file, this.get('fileTypeWhitelist'));
      this.set('isInvalid', invalid);
      if (this.fileChanged) {
        this.fileChanged(e, file, invalid);
      }
    },
    fileDismissed() {
      this.set('file', null);
      if (this.fileDismissed) {
        this.fileDismissed();
      }
    }
  }
});